import { useSwivuKanban } from "@/react/hooks/useSwivuKanban";
import { Kanban, KanbanBoard, KanbanColumn, KanbanItem, KanbanItemHandle, KanbanOverlay, useKanbanColumnContext } from "@/modern/components/ui/kanban";
import { Badge } from "@/modern/components/ui/badge";
import { Button } from "@/modern/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/modern/components/ui/collapsible";
import { ChevronRight, ChevronLeft, GripVertical, LayoutDashboard, X } from "lucide-react";
import { cn } from "../utils";
import { useVirtualizer } from "@tanstack/react-virtual";
import React, { useRef, useCallback, useEffect, useState } from "react";
import { getFontSize } from "@/react/context/bryntumContext";
import { Hitbox } from "./ui/hitbox";
import { getOriginalRecord } from "@/other/utils/utils";

const COLUMN_GAP_PX = 16;
const COLLAPSED_COLUMN_WIDTH_PX = 64;

function KanbanEmptyState({ showIcon = true, message, description, variant = "board" }: { showIcon?: boolean; message: string; description?: string; variant?: "board" | "column" }) {
  const isBoard = variant === "board";
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center text-center",
        isBoard ? "h-full m-4" : "py-12 px-4"
      )}
    >
      <div
        className={cn(
          "flex flex-col items-center gap-6",
          isBoard
            ? "rounded-2xl border border-dashed border-muted-foreground/30 bg-muted/40 px-8 py-8 shadow-sm w-full max-w-sm"
            : "gap-3"
        )}
      >
        {showIcon && <div
          className={cn(
            "flex items-center justify-center rounded-full",
            isBoard ? "mx-auto h-12 w-12 bg-primary/10 text-primary" : "h-12 w-12 bg-muted/50 text-muted-foreground"
          )}
        >
          <LayoutDashboard className="size-6" strokeWidth={1.5} />
        </div>}
        <div className="space-y-2">
          <h3
            className={cn(
              "text-base font-semibold text-foreground",
              !isBoard && "text-sm font-medium text-muted-foreground"
            )}
          >
            {message}
          </h3>
          {description && <p className="text-sm text-muted-foreground">{description}</p>}
        </div>
      </div>
    </div>
  );
}

interface KanbanWrapperProps extends ReturnType<typeof useSwivuKanban> {
  itemRenderer?: (data: any) => React.ReactNode;
  emptyText?: string;
  onItemDblClick?: (item: any) => void;
}

const getItemValueStable = (item: any) => item.id;

export function KanbanWrapper(props: KanbanWrapperProps) {
  const { columns, handleChange, handlePersist, toggleColumnCollapse, collapsedColumns, isKanbanMode, itemRenderer, emptyText = "No items", onItemDblClick, removeFromCurrentColumn, getFilteredHiddenCountByColumnId } = props;

  // Debounce hidden count to avoid a brief "1 hidden" flash when moving array-value items:
  // the store updates immediately on drop but columns state updates async, so for one frame
  // totalInColumn (from store) > visibleInColumn (from stale columns) and the badge appears then disappears.
  const [filteredHiddenByColumn, setFilteredHiddenByColumn] = useState<Record<string, number>>({});
  useEffect(() => {
    if (!columns?.length) {
      setFilteredHiddenByColumn({});
      return;
    }
    const t = window.setTimeout(() => {
      setFilteredHiddenByColumn(getFilteredHiddenCountByColumnId(columns));
    }, 350);
    return () => window.clearTimeout(t);
  }, [columns, getFilteredHiddenCountByColumnId]);
  const parentRef = useRef<HTMLDivElement>(null);
  const getItemValue = useCallback(getItemValueStable, []);

  const expandedWidth = getFontSize() * 20;
  const virtualizer = useVirtualizer({
    count: columns?.length ?? 0,
    getScrollElement: () => parentRef.current,
    estimateSize: () => expandedWidth + COLUMN_GAP_PX,
    overscan: 4,
    horizontal: true
  });

  const virtualColumns = virtualizer.getVirtualItems();


  if (!isKanbanMode) {
    return null;
  }

  if (!columns || columns.length === 0) {
    return <KanbanEmptyState message="No columns found" description="Use settings to manage columns for this grouper." variant="board" />;
  }

  return (
    <Kanban
      columns={columns}
      onColumnsChange={handleChange}
      onColumnsPersist={handlePersist}
      getItemValue={getItemValue}
      disableItemSorting={true}
      flatCursor={true}
    >
      <KanbanBoard className="flex flex-col px-4 py-4">
        <div
          ref={parentRef}
          className="flex-1 min-h-0 overflow-x-auto overflow-y-hidden"
          style={{ width: "100%", height: "100%" }}
        >
          <div
            style={{
              width: `${virtualizer.getTotalSize()}px`,
              height: "100%",
              position: "relative",
            }}
          >
            {virtualColumns.map((virtualColumn) => {
              const column = columns[virtualColumn.index];
              const isCollapsed = collapsedColumns[column.id];
              return (
                <Collapsible
                  key={column.id}
                  open={!isCollapsed}
                  onOpenChange={(open) => {
                    toggleColumnCollapse(String(column.id));
                    const willBeCollapsed = !open;
                    const width = (willBeCollapsed ? COLLAPSED_COLUMN_WIDTH_PX : expandedWidth) + COLUMN_GAP_PX;
                    virtualizer.resizeItem(virtualColumn.index, width);
                  }}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    height: "100%",
                    width: `${virtualColumn.size}px`,
                    transform: `translateX(${virtualColumn.start}px)`
                  }}
                >
                  <KanbanColumn value={column.id} className={cn(isCollapsed ? "w-16" : "w-[20rem]", "transition-all duration-300 bg-muted border rounded-sm p-0 h-full")} style={{ marginRight: COLUMN_GAP_PX }}>
                    <div className={cn("flex items-center justify-between border-b py-3 px-4", isCollapsed ? "flex-col-reverse py-4 gap-2 border-b-0" : "")}>
                      <div className={cn("flex items-center gap-3 min-w-0 flex-1", isCollapsed ? "flex-col" : "")}>
                        <span title={column.title} className={cn("font-semibold text-sm", isCollapsed ? "[writing-mode:vertical-lr]" : "truncate min-w-0")}>{column.title}</span>
                        <div className={cn("flex items-center gap-1.5", isCollapsed && "flex-col justify-center")}>
                          <Badge variant="muted" size="sm" className={cn("leading-none border-none", isCollapsed && "min-w-0 px-1.5")}>
                            {column.data.length}
                          </Badge>
                          {filteredHiddenByColumn[column.id] != null && filteredHiddenByColumn[column.id] > 0 && (
                            <Badge variant="secondary" size="sm" className={cn("leading-none border-none px-1.5 py-1 text-xs bg-amber-500/20 hover:bg-amber-500/20 text-amber-600 dark:text-amber-400 truncate", isCollapsed && "px-1 min-w-0")} title={`${filteredHiddenByColumn[column.id]} hidden`}>
                              {isCollapsed ? `${filteredHiddenByColumn[column.id]}h` : `${filteredHiddenByColumn[column.id]} hidden`}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <CollapsibleTrigger asChild>
                        <Button variant="ghost" size="icon-sm">
                          {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
                        </Button>
                      </CollapsibleTrigger>
                    </div>
                    <CollapsibleContent className="flex flex-col gap-3 h-full overflow-hidden">
                      {column.data && column.data.length > 0 ? (
                        <VirtualizedColumnContent
                          data={column.data}
                          columnValue={column.value}
                          itemRenderer={itemRenderer}
                          onItemDblClick={onItemDblClick}
                          onRemoveFromColumn={removeFromCurrentColumn}
                        />
                      ) : (
                        <KanbanEmptyState showIcon={false} message={emptyText} variant="column" />
                      )}
                    </CollapsibleContent>
                  </KanbanColumn>
                </Collapsible>
              );
            })}
          </div>
        </div>
      </KanbanBoard>
      <KanbanOverlay>
        {({ value, variant, getItemBaseId, isOverInvalidDropTarget }) => {
          if (variant === "column") {
            const column = columns.find(col => col.id === value);
            if (!column) return null;
            const isCollapsed = collapsedColumns[column.id];

            return (
              <KanbanColumn value={column.id} className={isCollapsed ? "w-16" : ""}>
                <div className={`flex items-center justify-between border-b px-3 pb-3 mb-3 ${isCollapsed ? "flex-col py-4 gap-2 border-b-0" : ""}`}>
                  <div className={`flex items-center gap-2 ${isCollapsed ? "flex-col" : ""}`}>
                    <span className={`font-semibold text-sm ${isCollapsed ? "[writing-mode:vertical-lr] rotate-180" : ""}`}>{column.title}</span>
                    <div className="flex items-center gap-1.5">
                      <Badge variant="primary-outline" size="sm">
                        {column.data.length}
                      </Badge>
                      {filteredHiddenByColumn[column.id] != null && filteredHiddenByColumn[column.id] > 0 && (
                        <Badge variant="secondary" size="sm" className="leading-none border-none px-1.5 py-1 text-xs bg-amber-500/20 text-amber-600 dark:text-amber-400">
                          {filteredHiddenByColumn[column.id]} hidden
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </KanbanColumn>
            );
          }

          // variant === "item" — value may be composite (columnId__itemId) when same item is in multiple columns
          const itemId = getItemBaseId ? getItemBaseId(value) : value;
          const allItems = columns.flatMap((col) => col.data);
          const idMatch = (d: any, id: unknown) => {
            const rid = d?.id ?? d?.get?.("id");
            return rid == id || String(rid) === String(id);
          };
          let data = allItems.find((d) => idMatch(d, itemId));
          if (!data && value !== itemId) {
            data = allItems.find((d) => idMatch(d, value));
          }
          return (
            <div
              className={cn(
                "min-w-[200px] min-h-[60px] overflow-hidden rounded-sm",
                isOverInvalidDropTarget && "ring-2 ring-destructive"
              )}
              style={{ opacity: 1 }}
            >
              {data ? (
                <>
                  <KanbanItemContent data={data} itemRenderer={itemRenderer} isOverlay />
                  {isOverInvalidDropTarget && <div className="absolute -top-7 left-1 rounded-md bg-destructive text-destructive-foreground text-xs px-2 py-1 rounded-xs" >Entity already exists in this column</div>}
                </>
              ) : (
                <div className="p-3 text-sm text-muted-foreground border border-border rounded-sm bg-background dark:bg-background/80">Dragging…</div>
              )}
            </div>
          );
        }}
      </KanbanOverlay>
    </Kanban>
  );
}

function getItemDataId(data: any): string | number | null {
  if (data == null) return null;
  const id = data?.id ?? data?.get?.("id");
  return id != null ? String(id) : null;
}

const VirtualizedColumnContent = React.memo(function VirtualizedColumnContent({
  data,
  columnValue,
  itemRenderer,
  onItemDblClick,
  onRemoveFromColumn,
}: {
  data: any[];
  columnValue: any;
  itemRenderer?: (data: any) => React.ReactNode;
  onItemDblClick?: (item: any) => void;
  onRemoveFromColumn?: (itemRecord: any, columnValue: any) => void;
}) {
  const parentRef = useRef<HTMLDivElement>(null);
  const { getItemIdForItem } = useKanbanColumnContext("VirtualizedColumnContent");

  const virtualizer = useVirtualizer({
    count: data.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 120,
    overscan: 5,
  });

  const items = virtualizer.getVirtualItems();

  return (
    <div
      ref={parentRef}
      className="flex-1 overflow-y-auto"
      style={{ height: '100%' }}
    >
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            transform: `translateY(${items[0]?.start ?? 0}px)`,
          }}
        >
          {items.map((virtualItem) => {
            const itemData = data[virtualItem.index];
            const itemId = getItemIdForItem(itemData);
            return (
              <div
                key={itemId}
                ref={virtualizer.measureElement}
                data-index={virtualItem.index}
                className="px-3 pb-3"
              >
                <KanbanItem value={itemId}>
                  <KanbanItemContent
                    data={itemData}
                    columnValue={columnValue}
                    itemRenderer={itemRenderer}
                    onItemDblClick={onItemDblClick}
                    onRemoveFromColumn={onRemoveFromColumn}
                  />
                </KanbanItem>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
});

function KanbanItemContentInner({
  data,
  columnValue,
  itemRenderer,
  onItemDblClick,
  onRemoveFromColumn,
  isOverlay = false,
}: {
  data: any;
  columnValue?: any;
  itemRenderer?: (data: any) => React.ReactNode;
  onItemDblClick?: (item: any) => void;
  onRemoveFromColumn?: (itemRecord: any, columnValue: any) => void;
  isOverlay?: boolean;
}) {
  const grip = (
    <span className="flex items-center cursor-grab active:cursor-grabbing touch-none text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity [&:focus]:opacity-100">
      <GripVertical className="h-4 w-4" aria-hidden />
    </span>
  );

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    const record = getOriginalRecord(data);
    onRemoveFromColumn?.(record, columnValue);
  };

  return (
    <div className="bg-background dark:bg-background/80 rounded-sm p-4 border border-border shadow-xs flex items-start gap-2 group relative overflow-hidden cursor-pointer" onDoubleClick={isOverlay ? undefined : (onItemDblClick ? () => onItemDblClick(getOriginalRecord(data)) : undefined)}>
      {isOverlay ? grip : (
        <Hitbox size="lg">
          <KanbanItemHandle className="cursor-grab" asChild>
            {grip}
          </KanbanItemHandle>
        </Hitbox>
      )}
      <div className="flex-1 min-w-0">
        {itemRenderer ? itemRenderer(data) : (
          <h4 className={cn("text-sm font-medium leading-tight text-foreground line-clamp-2", data?.archived && "line-through")}>
            {data.name || "N/A"}
          </h4>
        )}
      </div>
      {!isOverlay && columnValue && onRemoveFromColumn != null && (
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="absolute top-0 right-0 rounded-bl-md rounded-tr-none rounded-tl-none rounded-br-none opacity-0 group-hover:opacity-100 transition-opacity shrink-0 size-6"
          onClick={handleRemove}
          aria-label="Remove from column"
        >
          <X className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
}

const KanbanItemContent = React.memo(KanbanItemContentInner, (prev, next) => {
  if (prev.isOverlay !== next.isOverlay) return false;
  if (prev.columnValue !== next.columnValue) return false;
  if (getItemDataId(prev.data) !== getItemDataId(next.data)) return false;
  if (prev.data !== next.data) return false;
  return prev.itemRenderer === next.itemRenderer && prev.onItemDblClick === next.onItemDblClick && prev.onRemoveFromColumn === next.onRemoveFromColumn;
});